import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Play } from "lucide-react";
import { useState } from "react";
import { api, type ImportJob } from "@/lib/api";
import { ErrorState } from "@/components/empty-state";
import { ShopLogo } from "@/components/shop-logo";

export const Route = createFileRoute("/admin/electronic-first")({
  component: AdminElectronicFirst,
});

const CURRENCIES = ["EUR", "GBP", "USD"] as const;

function AdminElectronicFirst() {
  const qc = useQueryClient();
  const [limit, setLimit] = useState("");
  const status = useQuery({
    queryKey: ["admin-electronicfirst-status"],
    queryFn: () => api.adminElectronicFirstStatus(),
    refetchInterval: 4000,
  });
  const run = useMutation({
    mutationFn: (o: { currency: (typeof CURRENCIES)[number]; dryRun?: boolean }) =>
      api.adminImportElectronicFirst({ ...o, limit: limit ? Number(limit) : undefined }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-electronicfirst-status"] }),
  });
  const data = status.data?.data;
  const jobs = data?.jobs ?? [];
  const running = (cur: string) =>
    jobs.some((j) => j.status === "running" && j.source.startsWith(`electronicfirst:${cur.toLowerCase()}`));

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">Electronic First import</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Imports the EUR, GBP and USD product feeds. Each currency is its own offer; account
            products and gift cards are never imported.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="Limit (optional)"
            className="w-40 rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
          {CURRENCIES.map((c) => (
            <div key={c} className="flex gap-1">
              <button
                onClick={() => run.mutate({ currency: c, dryRun: true })}
                disabled={running(c) || run.isPending}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-2 text-xs font-semibold disabled:opacity-50"
              >
                <FlaskConical className="h-3.5 w-3.5" /> Dry {c}
              </button>
              <button
                onClick={() => run.mutate({ currency: c })}
                disabled={running(c) || run.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50"
              >
                <Play className="h-3.5 w-3.5" /> {running(c) ? "Running…" : `Import ${c}`}
              </button>
            </div>
          ))}
        </div>
      </header>

      {run.isError && <ErrorState message={(run.error as Error).message} />}
      {status.isError && <ErrorState message={(status.error as Error).message} />}

      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border bg-card p-4 text-sm">
        <ShopLogo shop={data?.shop ?? { name: "Electronic First", slug: "electronic-first", logoUrl: null }} size="md" />
        <span>{data?.shop?.name ?? "Not created yet — created on first non-dry run"}</span>
        {(data?.offerCounts ?? []).map((c) => (
          <span key={c.currency} className="font-mono text-xs text-muted-foreground">
            {c.currency}: {c.count.toLocaleString()} active
          </span>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Started</th>
              <th className="px-4 py-3 text-right">Matched</th>
              <th className="px-4 py-3 text-right">Unmatched</th>
              <th className="px-4 py-3 text-right">Failed</th>
              <th className="px-4 py-3 text-right">Imported</th>
              <th className="px-4 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-3 text-muted-foreground">No imports yet.</td></tr>
            )}
            {jobs.map((j: ImportJob) => (
              <tr key={j.id} className="border-t border-border/60">
                <td className="px-4 py-2">{j.source}</td>
                <td className={`px-4 py-2 ${j.status === "completed" ? "text-success" : j.status === "failed" ? "text-destructive" : "text-primary"}`}>{j.status}</td>
                <td className="px-4 py-2 text-muted-foreground">{new Date(j.startedAt).toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono">{(j.matchedItems ?? 0).toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono">{(j.unmatchedItems ?? 0).toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono text-destructive">{j.failedItems.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono text-success">{j.importedItems.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono">{j.totalItems.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
